import React, { useState, useEffect, useRef } from 'react';
import { Trans, t } from '@lingui/macro';
import { X, Clock, Calendar, Trash2, FileText, Loader2, Play, Pause, Users, Pencil } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { My } from '../../services/My';
import { Fire, getFunc } from '../../services/FireClient';
import { AiMinutesDoc } from '../../services/FireClientAiMemo';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { analyzeMinutes } from '../../services/AiMemoAnalyzer';
import { IconPlus, IconSparkles } from '../components/FigmaIcons';

interface AiMinutesDetailProps {
    roomId: string;
    minutesId: string;
    onBack: () => void;
}

const AiMinutesDetail: React.FC<AiMinutesDetailProps> = ({ roomId, minutesId, onBack }) => {
    const [minutes, setMinutes] = useState<AiMinutesDoc | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [aiStatus, setAiStatus] = useState<'idle' | 'analyzing' | 'done' | 'error'>('idle');
    const [attendeeInput, setAttendeeInput] = useState('');
    // 제목 인라인 편집 — 자동 생성된 제목을 사용자가 고칠 수 있게
    const [titleDraft, setTitleDraft] = useState<string | null>(null);
    const [titleSaving, setTitleSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const aiTriggeredRef = useRef(false);

    // roomId from props

    // --- 제목 편집 ---
    const saveTitle = async () => {
        if (titleDraft === null || !minutes) return;
        const next = titleDraft.trim().slice(0, 120);
        // 빈 제목이면 저장하지 않고 되돌린다 — 목록에서 식별할 수 없게 되기 때문
        if (!next || next === minutes.title) { setTitleDraft(null); return; }
        setTitleSaving(true);
        try {
            await Fire.aiMemo.updateMinutes(roomId, minutesId, { title: next });
            setTitleDraft(null);
        } catch (err) {
            console.warn('[Detail] 제목 저장 실패:', err);
        } finally {
            setTitleSaving(false);
        }
    };

    // --- 참석자 관리 ---
    const addAttendee = async (name: string) => {
        if (!name.trim() || !minutes) return;
        const current = minutes.attendees || [];
        if (current.includes(name.trim())) return;
        const updated = [...current, name.trim()];
        await Fire.aiMemo.updateMinutes(roomId, minutesId, { attendees: updated });
        setAttendeeInput('');
    };

    const removeAttendee = async (name: string) => {
        if (!minutes) return;
        const updated = (minutes.attendees || []).filter(a => a !== name);
        await Fire.aiMemo.updateMinutes(roomId, minutesId, { attendees: updated });
    };

    const handleAttendeeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addAttendee(attendeeInput);
        }
    };

    useEffect(() => {
        if (!roomId || !minutesId) return;

        setIsLoading(true);
        const unsub = Fire.aiMemo.subscribeToSingleMinutes(roomId, minutesId, (doc) => {
            setMinutes(doc);
            setIsLoading(false);

            // 자동 복구: sttTranscript 있지만 minutes 없으면 AI 분석 자동 실행
            if (doc && !doc.minutes && (doc as any).sttTranscript && !aiTriggeredRef.current) {
                aiTriggeredRef.current = true;
                runAutoAnalysis(doc);
            }
        });

        const fetchAudio = async () => {
            try {
                const storage = getStorage();
                const fileRefWebM = ref(storage, `ai-minutes/${roomId}/${minutesId}/recording.webm`);
                try {
                    const url = await getDownloadURL(fileRefWebM);
                    setAudioUrl(url);
                } catch {
                    // fallback to mp4 (iOS Safari)
                    const fileRefMp4 = ref(storage, `ai-minutes/${roomId}/${minutesId}/recording.mp4`);
                    const url = await getDownloadURL(fileRefMp4);
                    setAudioUrl(url);
                }
            } catch (e) {
                // Ignore if not found
            }
        };
        fetchAudio();

        return () => unsub();
    }, [roomId, minutesId]);

    /** STT 완료됐지만 AI 분석이 안 된 경우 자동 실행 */
    const runAutoAnalysis = async (doc: AiMinutesDoc) => {
        const sttText = (doc as any).sttTranscript as string;
        if (!sttText) return;

        setAiStatus('analyzing');
        try {
            const result = await analyzeMinutes(sttText, doc.language || 'ko', doc.duration);
            const cleanData = JSON.parse(JSON.stringify({
                title: result.title || '',
                minutes: result.minutes || {},
                tags: result.tags || [],
                transcript: sttText,
                status: 'completed',
            }));
            await Fire.aiMemo.updateMinutes(roomId, minutesId, cleanData);
            setAiStatus('done');
        } catch (err) {
            console.warn('[Detail] Auto AI analysis failed:', err);
             setAiStatus('error');
        }
    };

    const retryStt = async () => {
        if (!minutes || !minutes.language) return;
        setAiStatus('analyzing'); // Use the same analyzing status to show overall spinner
        try {
            // Update UI optimistically
            await Fire.aiMemo.updateMinutes(roomId, minutesId, { sttStatus: 'processing', sttError: '' });
            
            // Check storage extension dynamically based on what URL was loaded, or try both. We can just pass the path.
            const ext = audioUrl && audioUrl.includes('recording.mp4') ? 'mp4' : 'webm';
            const storagePath = `ai-minutes/${roomId}/${minutesId}/recording.${ext}`;

            const transcribeAudio = getFunc('transcribeAudio');
            const sttResult = await transcribeAudio({
                roomId,
                minutesId,
                storagePath,
                language: minutes.language,
                enableDiarization: true,
            });

            const sttTranscript = (sttResult?.data as any)?.transcript || '';
            if (sttTranscript) {
                await runAutoAnalysis({ ...minutes, sttTranscript } as any);
            } else {
                setAiStatus('idle'); // Just reset if silent
            }
        } catch (e: any) {
            console.error('[Detail] STT Retry failed:', e);
            await Fire.aiMemo.updateMinutes(roomId, minutesId, { sttStatus: 'failed', sttError: e.message });
            setAiStatus('idle');
        }
    };

    const handleDelete = async () => {
        try {
            await Fire.aiMemo.deleteMinutes(roomId, minutesId);
            onBack();
        } catch (e) {
            console.error('Failed to delete minutes:', e);
        }
    };

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = Math.floor(seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const seekTo = (seconds: number) => {
        if (audioRef.current) {
            audioRef.current.currentTime = seconds;
            if (!isPlaying) {
                audioRef.current.play();
                setIsPlaying(true);
            }
        }
    };

    const togglePlay = () => {
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleString();
    };

    if (isLoading) {
        return (
            <div className="p-4 md:p-6 lg:p-8 w-full">
                <div className="text-center py-16">
                    <div className="w-8 h-8 border-2 border-ai border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="text-sm text-muted-foreground mt-3"><Trans>Loading meeting minutes...</Trans></p>
                </div>
            </div>
        );
    }

    if (!minutes) {
        return (
            <div className="p-4 md:p-6 lg:p-8 w-full">
                <div className="text-center py-16 text-muted-foreground">
                    <FileText className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-sm"><Trans>Meeting minutes not found.</Trans></p>
                    <Button variant="outline" className="mt-4" onClick={onBack}>
                        <X className="w-4 h-4 mr-1" />
                        <Trans>Back to list</Trans>
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col w-full max-h-[90vh]">
            {/* Header — sticky */}
            <div className="flex items-center justify-between px-4 py-3 md:px-6 border-b bg-background sticky top-0 z-10 shrink-0">
                <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={onBack} className="text-muted-foreground hover:text-foreground shrink-0 -ml-1">
                        <X className="w-5 h-5" />
                    </Button>
                    <span className="text-sm font-semibold text-foreground"><Trans>Meeting Minutes</Trans></span>
                </div>
                <div className="flex items-center gap-1">
                    <span className={`px-2.5 py-0.5 rounded-full text-meta-sm font-medium ${minutes.status === 'completed'
                        ? 'bg-badge-success-bg text-badge-success'
                        : minutes.sttStatus === 'failed'
                            ? 'bg-destructive/10 text-destructive'
                            : 'bg-badge-caution-bg text-badge-caution'
                        }`}>
                        {minutes.status === 'completed' ? t`Completed` :
                            minutes.sttStatus === 'failed' ? t`Failed` : t`Analyzing`}
                    </span>
                    <Button variant="ghost" size="icon" onClick={() => setShowDeleteConfirm(true)} className="text-destructive hover:text-destructive shrink-0">
                        <Trash2 className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            {/* Scrollable content */}
            <div className="p-4 md:p-6 lg:p-8 space-y-5 overflow-y-auto flex-1">

                {/* Title — 클릭하면 편집 (자동 생성 제목을 사용자가 고칠 수 있다) */}
                {titleDraft === null ? (
                    <h2 className="group flex items-start gap-2 text-xl font-bold text-foreground">
                        <span className="min-w-0 flex-1 break-words">{minutes.title || t`Meeting Minutes`}</span>
                        <button type="button" title={t`Edit title`} aria-label={t`Edit title`}
                            onClick={() => setTitleDraft(minutes.title || '')}
                            className="mt-1 shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover:opacity-100">
                            <Pencil className="h-4 w-4" />
                        </button>
                    </h2>
                ) : (
                    <div className="flex items-center gap-2">
                        <input autoFocus value={titleDraft} maxLength={120} disabled={titleSaving}
                            onChange={(e) => setTitleDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); void saveTitle(); }
                                if (e.key === 'Escape') setTitleDraft(null);
                            }}
                            onBlur={() => { void saveTitle(); }}
                            className="min-w-0 flex-1 rounded-control border border-border bg-background px-2 py-1 text-xl font-bold text-foreground" />
                    </div>
                )}

                {/* Meta Info */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-card-foreground">
                        <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div>
                            <p className="text-meta-sm text-muted-foreground font-medium"><Trans>Meeting Date</Trans></p>
                            <p className="font-medium">{formatDate(minutes.created)}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-card-foreground">
                        <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div>
                            <p className="text-meta-sm text-muted-foreground font-medium"><Trans>Duration</Trans></p>
                            <p className="font-medium">{formatTime(minutes.duration || 0)}</p>
                        </div>
                    </div>
                </div>

                {/* Attendees — 수동 입력 */}
                <div>
                    <p className="text-meta-sm text-muted-foreground font-medium mb-1.5 flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        <Trans>Attendees</Trans>
                    </p>
                    <div className="flex flex-wrap gap-1.5 items-center">
                        {(minutes.attendees || []).map((name, i) => (
                            <span
                                key={i}
                                className="inline-flex items-center gap-1 text-xs bg-muted rounded-full px-2.5 py-1 text-foreground group"
                            >
                                {name}
                                <button
                                    onClick={() => removeAttendee(name)}
                                    className="text-muted-foreground hover:text-destructive transition-colors"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                        <div className="inline-flex items-center gap-1">
                            <input
                                type="text"
                                value={attendeeInput}
                                onChange={(e) => setAttendeeInput(e.target.value)}
                                onKeyDown={handleAttendeeKeyDown}
                                placeholder={t`Add attendee`}
                                className="text-xs border-0 border-b border-border focus:border-ai outline-none bg-transparent py-0.5 px-1 w-24 placeholder:text-muted-foreground"
                            />
                            {attendeeInput.trim() && (
                                <button onClick={() => addAttendee(attendeeInput)} className="text-ai hover:text-ai/80">
                                    <IconPlus className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* AI Analysis Status (자동 복구 / 재분석) */}
                {aiStatus === 'analyzing' && (
                    <div className="p-3 bg-badge-info-bg border border-badge-info/20 rounded-lg text-sm text-badge-info flex items-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <Trans>Generating AI minutes...</Trans>
                    </div>
                )}
                {aiStatus === 'error' && (
                    <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive flex items-center gap-2">
                        <span><Trans>⚠️ AI analysis failed</Trans></span>
                        <button
                            onClick={() => { aiTriggeredRef.current = false; if (minutes) runAutoAnalysis(minutes); }}
                            className="ml-auto text-xs font-semibold underline hover:text-destructive"
                        >
                            <Trans>Retry</Trans>
                        </button>
                    </div>
                )}
                {!minutes.minutes && (minutes as any).sttTranscript && aiStatus === 'idle' && (
                    <div className="p-3 bg-badge-caution-bg border border-badge-caution/20 rounded-lg text-sm text-badge-caution flex items-center gap-2">
                        <IconSparkles className="w-3.5 h-3.5" />
                        <Trans>Transcript is ready but AI summary has not been generated yet.</Trans>
                        <button
                            onClick={() => { if (minutes) runAutoAnalysis(minutes); }}
                            className="ml-auto text-xs font-semibold underline hover:text-badge-caution/80"
                        >
                            <Trans>AI analysis started</Trans>
                        </button>
                    </div>
                )}

                {/* AI Summary */}
                {minutes.minutes && (
                    <Card className="p-4">
                        <h4 className="text-xs font-semibold text-muted-foreground mb-3 flex items-center gap-1">
                            <IconSparkles className="w-3 h-3" />
                            <Trans>AI Meeting Summary</Trans>
                        </h4>


                        {/* Agenda */}
                        {minutes.minutes.agenda && (
                            <div className="mb-3">
                                <p className="text-xs font-medium text-muted-foreground"><Trans>Agenda</Trans></p>
                                <p className="text-sm text-foreground mt-1">{minutes.minutes.agenda}</p>
                            </div>
                        )}

                        {/* Key Discussions */}
                        {minutes.minutes.keyDiscussions && minutes.minutes.keyDiscussions.length > 0 && (
                            <div className="mb-3">
                                <p className="text-xs font-medium text-muted-foreground"><Trans>Key Discussions</Trans></p>
                                <ul className="list-disc list-inside mt-1 space-y-1">
                                    {minutes.minutes.keyDiscussions.map((item: any, i: number) => (
                                        <li key={i} className="text-sm text-foreground">
                                            {typeof item.timestamp === 'number' && (
                                                <button
                                                    onClick={() => seekTo(item.timestamp)}
                                                    className="text-ai hover:text-ai/80 font-mono text-xs mr-1.5 hover:underline cursor-pointer"
                                                    title={t`Jump to this section`}
                                                >
                                                    {formatTime(item.timestamp)}
                                                </button>
                                            )}
                                            <span className="font-medium">{item.topic}</span>: {item.detail}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Decisions */}
                        {minutes.minutes.decisions && minutes.minutes.decisions.length > 0 && (
                            <div className="mb-3">
                                <p className="text-xs font-medium text-muted-foreground"><Trans>Decisions</Trans></p>
                                <ul className="mt-1 space-y-1">
                                    {minutes.minutes.decisions.map((item: string, i: number) => (
                                        <li key={i} className="text-sm text-foreground flex items-start gap-2">
                                            <span className="text-green-500 mt-0.5">✓</span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Action Items */}
                        {minutes.minutes.actionItems && minutes.minutes.actionItems.length > 0 && (
                            <div className="mb-3">
                                <p className="text-xs font-medium text-muted-foreground"><Trans>Action Items</Trans></p>
                                <ul className="mt-1 space-y-1">
                                    {minutes.minutes.actionItems.map((item, i) => (
                                        <li key={i} className="text-sm text-foreground flex items-start gap-2">
                                            <span className="mt-1 w-4 h-4 rounded border border-border shrink-0" />
                                            <span>
                                                {item.task}
                                                {item.assignee && <span className="text-xs text-muted-foreground ml-1">({item.assignee})</span>}
                                                {item.deadline && <span className="text-xs text-purple-500 ml-1">~{item.deadline}</span>}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Pending Items */}
                        {minutes.minutes.pendingItems && minutes.minutes.pendingItems.length > 0 && (
                            <div>
                                <p className="text-xs font-medium text-muted-foreground"><Trans>Pending Items</Trans></p>
                                <ul className="mt-1 space-y-1">
                                    {minutes.minutes.pendingItems.map((item: string, i: number) => (
                                        <li key={i} className="text-sm text-foreground flex items-start gap-2">
                                            <span className="text-amber-500 mt-0.5">⏳</span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </Card>
                )}

                {/* Custom Audio Player — 회의요약과 고정밀 녹취록 사이 */}
                {audioUrl && (
                    <div>
                        <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                            <Trans>Play Audio Recording</Trans>
                        </h4>
                        <audio
                            ref={audioRef}
                            src={audioUrl}
                            onTimeUpdate={() => {
                                const ct = audioRef.current?.currentTime || 0;
                                setCurrentTime(ct);
                                if (duration === 0 || !isFinite(duration)) {
                                    setDuration(Math.max(ct, minutes?.duration || 0));
                                }
                            }}
                            onLoadedMetadata={() => {
                                const d = audioRef.current?.duration || 0;
                                if (isFinite(d) && d > 0) {
                                    setDuration(d);
                                } else {
                                    setDuration(minutes?.duration || 0);
                                }
                            }}
                            onDurationChange={() => {
                                const d = audioRef.current?.duration || 0;
                                if (isFinite(d) && d > 0) setDuration(d);
                            }}
                            onEnded={() => setIsPlaying(false)}
                            className="hidden"
                        />
                        <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl border">
                            <button
                                onClick={togglePlay}
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-ai text-on-primary-text hover:bg-ai/80 shrink-0"
                            >
                                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                            </button>
                            <span className="text-xs font-mono text-muted-foreground w-10 text-right shrink-0">
                                {formatTime(currentTime)}
                            </span>
                            <input
                                type="range"
                                min={0}
                                max={duration || 1}
                                step={0.1}
                                value={currentTime}
                                onChange={(e) => {
                                    const t = parseFloat(e.target.value);
                                    setCurrentTime(t);
                                    if (audioRef.current) audioRef.current.currentTime = t;
                                }}
                                className="flex-1 h-1.5 accent-ai cursor-pointer"
                            />
                            <span className="text-xs font-mono text-muted-foreground w-10 shrink-0">
                                {formatTime(duration)}
                            </span>
                        </div>
                    </div>
                )}

                {/* Cloud STT Transcript (고정밀 화자분리) — 타임스탬프 클릭 가능 */}
                {(minutes as any).sttSpeakers && (minutes as any).sttSpeakers.length > 0 ? (
                    <div>
                        <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                            <IconSparkles className="w-3 h-3" />
                            <Trans>High-precision Transcript (Speaker Diarization)</Trans>
                        </h4>
                        <div className="p-4 bg-muted/50 rounded-xl border space-y-2">
                            {(minutes as any).sttSpeakers.map((seg: any, i: number) => (
                                <div key={i} className="text-sm leading-relaxed">
                                    <button
                                        onClick={() => seekTo(seg.startTime || 0)}
                                        className="text-ai hover:text-ai/80 font-mono text-xs mr-2 hover:underline cursor-pointer"
                                        title={t`Jump to this section`}
                                    >
                                        {seg.formattedTime || formatTime(seg.startTime || 0)}
                                        {seg.endTime ? `~${formatTime(seg.endTime)}` : ''}
                                    </button>
                                    <span className="text-xs font-semibold text-badge-info mr-1">
                                        [<Trans>Speaker {seg.speaker}</Trans>]
                                    </span>
                                    <span className="text-foreground">{seg.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (minutes as any).sttTranscript ? (
                    <div>
                        <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                            <IconSparkles className="w-3 h-3" />
                            <Trans>High-precision Transcript (Speaker Diarization)</Trans>
                        </h4>
                        <div className="p-4 bg-muted/50 rounded-xl border">
                            <p className="text-sm whitespace-pre-wrap text-foreground leading-relaxed">
                                {(minutes as any).sttTranscript}
                            </p>
                        </div>
                    </div>
                ) : null}



                {/* STT Processing Status */}
                {minutes.sttStatus === 'processing' && (
                    <div className="p-3 bg-badge-info-bg border border-badge-info/20 rounded-lg text-xs text-badge-info flex items-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                        <span className="flex-1"><Trans>AI high-precision speech recognition in progress... Results will appear automatically.</Trans></span>
                    </div>
                )}
                
                {/* STT Failed Status */}
                {minutes.sttStatus === 'failed' && (
                    <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive flex items-start gap-2">
                        <span className="mt-0.5"><Trans>⚠️</Trans></span>
                        <div className="flex-1">
                            <p className="font-semibold text-sm mb-1"><Trans>High-precision speech recognition failed</Trans></p>
                            <p className="text-xs opacity-90 mb-2">{minutes.sttError || t`An error occurred on the server.`}</p>
                            <button
                                onClick={() => { aiTriggeredRef.current = false; retryStt(); }}
                                disabled={aiStatus === 'analyzing'}
                                className="text-xs font-semibold underline hover:text-destructive/80 outline-none disabled:opacity-50"
                            >
                                {aiStatus === 'analyzing' ? t`Retrying...` : t`Retry Speech Recognition`}
                            </button>
                        </div>
                    </div>
                )}

                {/* Full Transcript (Web Speech API 기본) — 고정밀이 있으면 접이식, 내용이 같으면 숨김 */}
                {(() => {
                    const hasStt = !!(minutes as any).sttTranscript;
                    const isSame = hasStt && minutes.transcript?.trim() === (minutes as any).sttTranscript?.trim();
                    if (isSame) return null; // 내용 동일하면 숨김
                    if (hasStt) {
                        return (
                            <details className="text-xs text-muted-foreground">
                                <summary className="cursor-pointer hover:text-card-foreground">
                                    <Trans>View basic transcript (Web Speech API)</Trans>
                                </summary>
                                <div className="p-4 bg-muted/50 rounded-xl border mt-2">
                                    <p className="text-sm whitespace-pre-wrap text-foreground leading-relaxed">
                                        {minutes.transcript}
                                    </p>
                                </div>
                            </details>
                        );
                    }
                    return (
                        <div>
                            <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                <Trans>Full Transcript</Trans>
                            </h4>
                            <div className="p-4 bg-muted/50 rounded-xl border">
                                <p className="text-sm whitespace-pre-wrap text-foreground leading-relaxed">
                                    {minutes.transcript}
                                </p>
                            </div>
                        </div>
                    );
                })()}

                {/* Tags */}
                {minutes.tags && minutes.tags.length > 0 && (
                    <div className="pt-4 border-t">
                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                            {minutes.tags.map((tag, i) => (
                                <span
                                    key={i}
                                    className="text-sm text-ai"
                                >
                                    #{tag.name}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle><Trans>Delete this meeting minutes?</Trans></AlertDialogTitle>
                        <AlertDialogDescription>
                            <Trans>This action cannot be undone.</Trans>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90 text-destructive-foreground">
                            <Trans>Delete</Trans>
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default AiMinutesDetail;
